import { useState, useEffect, useCallback, useRef } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { useLocation } from 'react-router-dom';
import { DataTable } from '../components/shared/DataTable';
import { ViewVoiceHistoryModal } from '../components/voices/ViewVoiceHistoryModal';
import { ChangeVoiceNameModal } from '../components/voices/ChangeVoiceNameModal';
import { SyncResultModal } from '../components/phone-numbers/SyncResultModal';
import { searchCallVoices, updateCallVoice, syncRetellActiveVoices } from '../services/callVoiceService';
import type { SyncTwilioResult } from '../services/callPhoneNumberService';
import { useProfileStore } from '../store/useProfileStore';
import { isCompanyShopSuperAdmin } from '../types/profile';
import { useBreadcrumbStore } from '../store/useBreadcrumbStore';
import { useRefresh } from '../contexts/RefreshContext';
import type { CallVoice, CallVoiceStatus } from '../types/callVoice';
import type { Column } from '../components/shared/DataTable';

function PlayButton({ url }: { url: string }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const toggle = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(url);
      audioRef.current.onended = () => setPlaying(false);
    }
    if (playing) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
    } else {
      audioRef.current.play();
      setPlaying(true);
    }
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  return (
    <button
      onClick={toggle}
      className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
      title={playing ? 'Stop' : 'Play'}
    >
      {playing ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <rect x="6" y="4" width="4" height="16" rx="1" />
          <rect x="14" y="4" width="4" height="16" rx="1" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 5v14l11-7z" />
        </svg>
      )}
    </button>
  );
}

const STATUS_PRIORITY: Record<string, number> = {
  new: 0, changed: 1, active: 2, inactive: 3, burned: 4, deleted: 5,
};

const columns: Column<CallVoice>[] = [
  { key: 'provider', label: 'Provider', render: (item) => item.provider.replace(/\b\w/g, (c) => c.toUpperCase()) },
  { key: 'status', label: 'Status' },
  {
    key: 'voiceName',
    label: 'Voice Name',
    render: (item) => (
      <div>
        <div>{item.name || item.voiceName}</div>
        <div className="text-xs text-gray-400">{item.voiceName}</div>
      </div>
    ),
  },
  {
    key: 'preview',
    label: 'Play',
    render: (item) => item.previewAudioUrl ? <PlayButton url={item.previewAudioUrl} /> : '—',
  },
  { key: 'age', label: 'Age' },
  { key: 'gender', label: 'Gender' },
  { key: 'accent', label: 'Accent' },
  { key: 'voiceId', label: 'Voice Id' },
];

function RowMenu({ item, onStatusChange, onChangeName }: {
  item: CallVoice;
  onStatusChange: (item: CallVoice, status: CallVoiceStatus) => void;
  onChangeName: (item: CallVoice) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const canSetActive = item.status === 'new' || item.status === 'burned' || item.status === 'inactive';
  const canSetInactive = item.status === 'new' || item.status === 'active' || item.status === 'burned';
  const canBurn = item.status === 'active';

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100 transition-colors"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[180px]">
          <button
            onClick={() => { setOpen(false); onChangeName(item); }}
            className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            Change Name
          </button>
          <button
            disabled={!canSetActive}
            onClick={() => { setOpen(false); onStatusChange(item, 'active'); }}
            className={`w-full text-left px-3 py-1.5 text-sm ${canSetActive ? 'text-gray-700 hover:bg-gray-50' : 'text-gray-300 cursor-not-allowed'}`}
          >
            Set Active
          </button>
          <button
            disabled={!canBurn}
            onClick={() => { setOpen(false); onStatusChange(item, 'burned'); }}
            className={`w-full text-left px-3 py-1.5 text-sm ${canBurn ? 'text-gray-700 hover:bg-gray-50' : 'text-gray-300 cursor-not-allowed'}`}
          >
            Burn
          </button>
          <button
            disabled={!canSetInactive}
            onClick={() => { setOpen(false); onStatusChange(item, 'inactive'); }}
            className={`w-full text-left px-3 py-1.5 text-sm ${canSetInactive ? 'text-gray-700 hover:bg-gray-50' : 'text-gray-300 cursor-not-allowed'}`}
          >
            Set Inactive
          </button>
        </div>
      )}
    </div>
  );
}

export default function VoicesPage() {
  const location = useLocation();
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const isSuperAdmin = isCompanyShopSuperAdmin(activeProfile);

  const [data, setData] = useState<CallVoice[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [viewHistoryId, setViewHistoryId] = useState<string | null>(null);
  const [changeNameId, setChangeNameId] = useState<string | null>(null);
  const [manageMenuOpen, setManageMenuOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncTwilioResult | null>(null);
  const manageRef = useRef<HTMLDivElement>(null);
  const setBreadcrumb = useBreadcrumbStore((s) => s.set);

  useEffect(() => {
    if (location.pathname !== '/voices') return;
    setBreadcrumb([{ label: 'Voices' }]);
  }, [location.pathname, setBreadcrumb]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const results = await searchCallVoices({}, { page, pageSize, orderBy: 'updated', order: 'desc' });
      setData(results);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  const sortedData = [...data].sort((a, b) => {
    const sa = STATUS_PRIORITY[a.status] ?? 99;
    const sb = STATUS_PRIORITY[b.status] ?? 99;
    if (sa !== sb) return sa - sb;
    return (a.voiceName || '').localeCompare(b.voiceName || '');
  });

  useEffect(() => { fetch(); }, [fetch]);

  const { registerRefreshHandler, unregisterRefreshHandler } = useRefresh();
  useEffect(() => {
    registerRefreshHandler('/voices', fetch);
    return () => unregisterRefreshHandler('/voices');
  }, [fetch, registerRefreshHandler, unregisterRefreshHandler]);

  useEffect(() => {
    if (!manageMenuOpen) return;
    function handleClick(e: MouseEvent) {
      if (manageRef.current && !manageRef.current.contains(e.target as Node)) setManageMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [manageMenuOpen]);

  const handleStatusChange = async (item: CallVoice, status: CallVoiceStatus) => {
    await updateCallVoice(item.id, { ...item, status });
    fetch();
  };

  const handleSyncRetell = async () => {
    setManageMenuOpen(false);
    setSyncing(true);
    try {
      const result = await syncRetellActiveVoices();
      setSyncResult(result);
      fetch();
    } finally {
      setSyncing(false);
    }
  };

  return (
    <IonPage>
      <IonContent>
        <div className="p-4 sm:p-6 lg:p-8">
          <DataTable
            title="Voices"
            columns={columns}
            data={sortedData}
            page={page}
            pageSize={pageSize}
            loading={loading}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            onRefresh={fetch}
            headerControls={isSuperAdmin ? (
              <div ref={manageRef} className="relative">
                <button
                  onClick={() => setManageMenuOpen(!manageMenuOpen)}
                  disabled={syncing}
                  className="px-2 py-1 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {syncing ? 'Syncing...' : 'Manage Providers'}
                </button>
                {manageMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[220px]">
                    <button
                      onClick={handleSyncRetell}
                      className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Sync Retell Active Voices
                    </button>
                  </div>
                )}
              </div>
            ) : undefined}
            actions={(item) => (
              <button
                onClick={() => setViewHistoryId(item.id)}
                className="text-xs px-2 py-1 rounded text-white transition-colors" style={{ backgroundColor: 'var(--ion-color-secondary)' }}
              >
                View History
              </button>
            )}
            rowMenu={isSuperAdmin ? (item) => (
              <RowMenu item={item} onStatusChange={handleStatusChange} onChangeName={(v) => setChangeNameId(v.id)} />
            ) : undefined}
          />

          {viewHistoryId && (
            <ViewVoiceHistoryModal
              callVoiceId={viewHistoryId}
              onClose={() => setViewHistoryId(null)}
            />
          )}

          {changeNameId && (
            <ChangeVoiceNameModal
              callVoiceId={changeNameId}
              onClose={() => setChangeNameId(null)}
              onSuccess={() => { setChangeNameId(null); fetch(); }}
            />
          )}

          {syncResult && (
            <SyncResultModal
              result={syncResult}
              onClose={() => setSyncResult(null)}
            />
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
